import type React from 'react';
import { useEffect, useState } from 'react';
import {
  KovResourcePicker,
  Modal,
} from '@kathondvla/react-shared-components/src/components';
import type { Content, ContentHref } from '@generalTypes/apiTypes';
import { formatDate, stripHtml } from '@newStore/genericHelpers';
import { fetchAllCmd } from '@store/commands/documentCommands';
import { getGoalIdentifier, getResourceKey, sortByReadorder } from '@store/helpers/documentHelpers';
import { useTypedSelector } from '../../hooks/useTypedSelector';
import { selectPreviousVersionContent } from '@newStore/llinkid/llinkidSelectors';
import { loadPreviousVersion } from '@newStore/llinkid/llinkidActions';
import { useDispatch } from 'react-redux';
import { selectRootHref } from '@newStore/documentUI/documentUISelectors';

const getGoals = async (rootHref) => {
  const goals = await fetchAllCmd(
    {
      root: rootHref,
      type: 'LLINKID_GOAL',
      limit: 500,
    },
    { fetchTreeAsLeaf: true },
  );
  return sortByReadorder(goals).map((goal) => ({
    display: `${getGoalIdentifier({
      goal,
      relations: goal.$$treeAsLeaf,
      isOdet: false,
    })} - ${stripHtml(goal.description)}`,
    href: goal.$$meta.permalink,
  }));
};

const getTips = async (rootHref) => {
  const goals = await fetchAllCmd(
    {
      root: rootHref,
      type: 'LLINKID_PEDAGOGICAL_TIP',
      limit: 500,
    },
    { fetchTreeAsLeaf: true },
  );
  return sortByReadorder(goals);
};

export interface LlinkidPreviousVersionModalProps {
  selectedValues?: string[];
  onClose?: () => void;
  onSubmit?: (response: {
    goalHref: ContentHref | undefined;
    previousVersionHref: ContentHref;
  }) => void;
}

const LlinkidPreviousVersionModal: React.FC<LlinkidPreviousVersionModalProps> = ({
  selectedValues = [],
  onClose = () => {},
  onSubmit,
}) => {
  const dispatch = useDispatch();

  const [selectedGoal, setSelectedGoal] = useState<ContentHref | undefined>();
  const [selectedTip, setSelectedTip] = useState<ContentHref | undefined>();

  const [goals, setGoals] = useState<
    | {
        display: string;
        href: ContentHref;
      }[]
    | null
  >(null);
  const [tips, setTips] = useState<(Content & { $$treeAsLeaf: Content[] })[] | null>(null);
  const rootHref = useTypedSelector((state) => selectRootHref(state));

  useEffect(() => {
    if (rootHref) {
      dispatch(loadPreviousVersion({ href: rootHref }));
    }
  }, [dispatch, rootHref]);

  const documents = useTypedSelector(
    (state) => rootHref && selectPreviousVersionContent(state, rootHref),
  );

  useEffect(() => {
    if (documents?.[0]) {
      getGoals(documents[0].$$meta.permalink).then((goals) => {
        setGoals(goals.filter((g) => !selectedValues.includes(getResourceKey(g.href))));
      });
      getTips(documents[0].$$meta.permalink).then((tips) => {
        setTips(tips);
      });
    }
  }, [documents, selectedValues]);

  const filteredTips = selectedGoal
    ? tips?.filter((tip) => {
        return tip.$$treeAsLeaf?.some((l) => l?.$$meta?.permalink === selectedGoal);
      })
    : tips;

  const onChangeGoal = (o) => {
    setSelectedGoal(o.href);
  };

  const onChangeTip = (o) => {
    setSelectedTip(o.$$meta.permalink);
  };

  const handleSubmit = () => {
    if (onSubmit && selectedTip) {
      onSubmit({
        goalHref: selectedGoal,
        previousVersionHref: selectedTip,
      });
    }
    onClose();
  };

  const formatDocument = (o) => {
    const option = o.$$expanded || o;
    let title = option.title;
    if (option.identifiers) {
      title += ` ${option.identifiers[0]}  `;
    }
    if (option.version) {
      title += ` (v${option.version.major})`;
    }
    if (option.issued) {
      title += ` (${formatDate(option.issued, false)})`;
    }
    return <div className="picker-result">{title}</div>;
  };

  const formatGoal = (o) => {
    const option = o.$$expanded || o;
    return option.display;
  };

  return (
    <div className="overflown-modal">
      <Modal.Header title={'Vervangt (uit vorige versie)'} onClose={onClose} />
      <Modal.Body>
        <div className="form-group">
          <div className="row">
            <div className="col-md-12">
              <label className="control-label">Document:</label>
              <KovResourcePicker
                clearable={false}
                multiple={false}
                disabled={true}
                placeholder={documents?.[0] ? formatDocument(documents[0]) : 'Laden...'}
              />
            </div>
          </div>
        </div>
        <div className="form-group">
          <div className="row">
            <div className="col-md-12">
              <label className="control-label">Gerelateerd doel:</label>
              {goals ? (
                <KovResourcePicker
                  defaultValue={selectedGoal}
                  onChange={onChangeGoal}
                  localData={goals}
                  optionValue={(option) => option.href}
                  placeholder={'Kies een gerelateerd doel'}
                  clearable={false}
                  multiple={false}
                  optionTemplate={formatGoal}
                  disabled={goals.length === 0}
                />
              ) : (
                <div className="shimmer narrowLine" />
              )}
            </div>
          </div>
        </div>
        <div className="form-group">
          <div className="row">
            <div className="col-md-12">
              <label className="control-label">Wenk: </label>
              {tips ? (
                <KovResourcePicker
                  defaultValue={selectedTip}
                  onChange={onChangeTip}
                  localData={filteredTips}
                  optionValue={(option) => option.$$meta.permalink}
                  placeholder={'Kies een te vervangen wenk'}
                  clearable={false}
                  multiple={false}
                  optionTemplate={(o) => (
                    <div
                      dangerouslySetInnerHTML={{
                        __html: o.description,
                      }}
                    />
                  )}
                />
              ) : (
                <div className="shimmer narrowLine" />
              )}
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button className="btn btn-link pull-left" onClick={onClose}>
          Annuleren
        </button>
        <button className="btn btn-primary" onClick={handleSubmit} disabled={!selectedTip}>
          Ok
        </button>
      </Modal.Footer>
    </div>
  );
};

LlinkidPreviousVersionModal.displayName = 'LlinkidPreviousVersionModal';

export default LlinkidPreviousVersionModal;
